import { createContext, PropsWithChildren, useContext, useMemo, useState } from "react";

import { DashboardOverviewConfig } from "@/domain/workspace/types/dashboard-overview-config";
import { type ChartColorSlotConfig, type DashboardExportProfileKey } from "@ark/shared";

/** V2 template features are gated behind the `enable-dashboard-template-v2` feature flag. */
export type BlockEnvironment = {
  dashboardId?: string;
  /** Parent (top-level) dashboard id; equals `dashboardId` except on sub-template embeds. Used for cross-template row navigation so the target resolves templates against the correct scope. */
  rootDashboardId?: string;
  workspaceId?: string;
  environment: "embed" | "preview" | "export";
  exportProfile?: DashboardExportProfileKey;
  templateParams?: { projectId?: string; climateActionId?: string };
  dashboardOverviewConfig?: DashboardOverviewConfig;
  dashboardNavigationConfig?: Record<string, unknown>;
  chartColorSlotConfig?: ChartColorSlotConfig;
  entityType?: "climateAction" | "project";
  /** Whether a climate-action template exists for this dashboard (used to gate row-click navigation). */
  hasClimateActionTemplate?: boolean;
  /** Whether a project template exists for this dashboard (used to gate row-click navigation). */
  hasProjectTemplate?: boolean;
  defaultLandingPageId?: string | null;
};

export type Config = {
  shouldScale: boolean;
  setShouldScale: (shouldScale: boolean) => void;
  viewportScale: number;
  setViewportScale: (viewportScale: number) => void;
};

export type BlockEnvironmentResult = BlockEnvironment & Config & { readonly isExportMode: boolean };

const BlockEnvironmentContext = createContext<(BlockEnvironment & Config) | null>(null);

const defaultBlockEnvironment: BlockEnvironment & Config = {
  environment: "preview",
  shouldScale: true,
  setShouldScale: () => {},
  viewportScale: 1,
  setViewportScale: () => {},
};

export function BlockEnvironmentProvider({
  value = defaultBlockEnvironment,
  children,
}: PropsWithChildren<{ value?: BlockEnvironment }>) {
  const [shouldScale, setShouldScale] = useState(true);
  const [viewportScale, setViewportScale] = useState(1);
  return (
    <BlockEnvironmentContext.Provider value={{ ...value, shouldScale, setShouldScale, viewportScale, setViewportScale }}>
      {children}
    </BlockEnvironmentContext.Provider>
  );
}

export function useBlockEnvironment(): BlockEnvironmentResult {
  const env = useContext(BlockEnvironmentContext) ?? defaultBlockEnvironment;
  return useMemo(() => ({ ...env, isExportMode: env.environment === "export" }), [env]);
}
